import React, { useContext, useEffect, useState } from 'react'

interface DelayUnmountProps {
  children: React.ReactNode
  delay?: number
  visible?: boolean
  initiallyVisible?: boolean
}

export function Delay({ delay = 300, children, visible = true, initiallyVisible = true }: DelayUnmountProps) {
  return (
    <DelayProvider initiallyVisible={initiallyVisible}>
      <DelayInner newVisible={visible} delay={delay}>
        {children}
      </DelayInner>
    </DelayProvider>
  )
}

function DelayInner({
  children,
  newVisible,
  delay,
}: {
  children: React.ReactNode
  newVisible: boolean
  delay: number
}) {
  const [render, shouldRender] = useState(newVisible)
  const { setVisible } = useDelayContext()

  useEffect(() => {
    const timeoutId: NodeJS.Timeout = setTimeout(() => {
      shouldRender(newVisible)
    }, delay)

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [newVisible, delay, setVisible])

  useEffect(() => {
    setVisible(newVisible)
  }, [newVisible, setVisible])

  return <>{render ? children : null}</>
}

interface DelayContextType {
  initiallyVisible?: boolean
  visible: boolean
  setVisible: (visible: boolean) => void
}

const defaultValue = {
  initiallyVisible: true,
  visible: true,
  setVisible: () => null,
}

const DelayContext = React.createContext<DelayContextType>(defaultValue)

interface DelayProviderProps {
  initiallyVisible: boolean
  children: React.ReactNode
}

export function DelayProvider({ initiallyVisible, children }: DelayProviderProps) {
  const [visible, setVisible] = useState(initiallyVisible)

  return (
    <DelayContext.Provider
      value={{
        visible,
        setVisible,
      }}
    >
      {children}
    </DelayContext.Provider>
  )
}

export const useDelayContext = () => {
  return useContext(DelayContext)
}
